import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Button,
  Alert,
  Divider,
  Paper,
  Chip
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  VolumeUp as VolumeUpIcon,
  CheckCircle as CheckCircleIcon,
  Block as BlockIcon,
  Help as HelpIcon
} from '@mui/icons-material';
import notificationHelper from '../../utils/notificationHelper';

const ChatSettings = () => {
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [desktopNotifications, setDesktopNotifications] = useState(false);
  const [soundNotifications, setSoundNotifications] = useState(true);

  // Load settings from localStorage
  useEffect(() => {
    const permission = notificationHelper.getPermissionStatus();
    setNotificationPermission(permission);

    const desktopEnabled = localStorage.getItem('chat_desktop_notifications') === 'true';
    const soundEnabled = localStorage.getItem('chat_sound_enabled') !== 'false'; // Default true

    setDesktopNotifications(desktopEnabled && permission === 'granted');
    setSoundNotifications(soundEnabled);
  }, []);

  // Request notification permission
  const handleRequestPermission = async () => {
    const permission = await notificationHelper.requestPermission();
    setNotificationPermission(permission);

    if (permission === 'granted') {
      setDesktopNotifications(true);
      localStorage.setItem('chat_desktop_notifications', 'true');

      // Show test notification
      notificationHelper.showNotification({
        title: 'Benachrichtigungen aktiviert!',
        body: 'Du erhältst nun Desktop-Benachrichtigungen für neue Nachrichten.',
        onClick: () => console.log('Test notification clicked')
      });
    }
  };

  // Toggle desktop notifications
  const handleDesktopToggle = (event) => {
    const enabled = event.target.checked;

    if (enabled && notificationPermission !== 'granted') {
      handleRequestPermission();
      return;
    }

    setDesktopNotifications(enabled);
    localStorage.setItem('chat_desktop_notifications', enabled.toString());
  };

  // Toggle sound notifications
  const handleSoundToggle = (event) => {
    const enabled = event.target.checked;
    setSoundNotifications(enabled);
    localStorage.setItem('chat_sound_enabled', enabled.toString());

    // Play test sound
    if (enabled) {
      try {
        const audio = new Audio('/sounds/message.mp3');
        audio.volume = 0.5;
        audio.play().catch(e => console.debug('Sound test failed'));
      } catch (e) {
        console.debug('Sound test failed');
      }
    }
  };

  // Permission status indicator
  const getPermissionStatus = () => {
    switch (notificationPermission) {
      case 'granted':
        return {
          icon: <CheckCircleIcon color="success" />,
          text: 'Berechtigung erteilt',
          color: 'success'
        };
      case 'denied':
        return {
          icon: <BlockIcon color="error" />,
          text: 'Berechtigung verweigert',
          color: 'error'
        };
      default:
        return {
          icon: <HelpIcon color="warning" />,
          text: 'Noch nicht angefragt',
          color: 'warning'
        };
    }
  };

  const permissionStatus = getPermissionStatus();

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <NotificationsIcon />
        Benachrichtigungen
      </Typography>

      {/* Permission Status */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Browser-Berechtigung
          </Typography>
          <Chip
            icon={permissionStatus.icon}
            label={permissionStatus.text}
            color={permissionStatus.color}
            size="small"
          />
        </Box>

        {notificationPermission === 'default' && (
          <Button
            fullWidth
            variant="contained"
            onClick={handleRequestPermission}
            sx={{ mt: 1 }}
          >
            Benachrichtigungen aktivieren
          </Button>
        )}

        {notificationPermission === 'denied' && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            Benachrichtigungen wurden blockiert. Bitte ändere die Browser-Einstellungen, um sie zu aktivieren.
          </Alert>
        )}
      </Paper>

      <Divider sx={{ my: 2 }} />

      {/* Desktop Notifications Toggle */}
      <Box sx={{ mb: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={desktopNotifications}
              onChange={handleDesktopToggle}
              disabled={notificationPermission !== 'granted'}
            />
          }
          label={
            <Box>
              <Typography variant="body1">Desktop-Benachrichtigungen</Typography>
              <Typography variant="caption" color="text.secondary">
                Zeige Popup-Benachrichtigungen auch wenn der Browser nicht sichtbar ist
              </Typography>
            </Box>
          }
        />
        {!notificationHelper.isNotificationSupported() && (
          <Alert severity="info" sx={{ mt: 1, ml: 4 }}>
            Dein Browser unterstützt keine Desktop-Benachrichtigungen
          </Alert>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Sound Notifications Toggle */}
      <Box>
        <FormControlLabel
          control={
            <Switch
              checked={soundNotifications}
              onChange={handleSoundToggle}
            />
          }
          label={
            <Box>
              <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <VolumeUpIcon fontSize="small" />
                Sound-Benachrichtigungen
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Spiele einen Sound ab wenn neue Nachrichten ankommen
              </Typography>
            </Box>
          }
        />
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Info Box */}
      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="body2">
          <strong>Hinweis:</strong> Benachrichtigungen werden nur angezeigt, wenn du in einem anderen Tab oder außerhalb des Browsers bist.
        </Typography>
      </Alert>
    </Box>
  );
};

export default ChatSettings;
